/**
 * Webhook subscription types — mirrors the backend service view in
 * `apps/operations/src/modules/webhooks/services/webhook-subscription.service.ts`.
 */

export interface WebhookSubscriptionView {
  id: string;
  tenantId: string;
  url: string;
  eventTypes: string[];
  apiVersion: string | null;
  enabled: boolean;
  description: string | null;
  lastDeliveredAt: string | null;
  lastFailureAt: string | null;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
  /**
   * Plaintext signing secret. Only present on the response from `create()`.
   */
  secret?: string;
}

export interface WebhookEventDefinition {
  value: string;
  label: string;
  description?: string;
}

export interface WebhookEventGroup {
  group: string;
  events: WebhookEventDefinition[];
}

export type WebhookDeliveryStatus = 'PENDING' | 'DELIVERED' | 'FAILED';

export interface WebhookDeliveryView {
  id: string;
  tenantId: string;
  subscriptionId: string;
  outboxEventId: string | null;
  eventType: string;
  payload: unknown;
  status: WebhookDeliveryStatus;
  attempts: number;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  responseStatus: number | null;
  responseBody: string | null;
  createdAt: string;
}

export interface WebhookDeliveryPage {
  data: WebhookDeliveryView[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateWebhookSubscriptionDto {
  url: string;
  eventTypes: string[];
  enabled?: boolean;
  description?: string;
}

export interface UpdateWebhookSubscriptionDto {
  url?: string;
  eventTypes?: string[];
  enabled?: boolean;
  description?: string;
}

export interface SetEnabledWebhookSubscriptionDto {
  enabled: boolean;
}

export const buildWebhookEventValueToLabel = (
  groups: WebhookEventGroup[] | undefined,
): Record<string, string> =>
  (groups ?? []).reduce<Record<string, string>>((acc, g) => {
    for (const e of g.events) acc[e.value] = e.label;
    return acc;
  }, {});
